import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/projects`;

const thStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '10px 14px', color: 'var(--text-hint)' };
const tdStyle: React.CSSProperties = { fontSize: 13, padding: '10px 14px' };

export default function ProjectCosting() {
  const { entity } = useGlobalEntity();
  const [projects, setProjects] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [pnl, setPnl] = useState<any>(null);
  const [pnlId, setPnlId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [tab, setTab] = useState('projects');
  const [timeEntries, setTimeEntries] = useState<any[]>([]);
  const [form, setForm] = useState({ project_code: '', project_name: '', customer_name: '', project_manager_name: '', project_type: 'FIXED_PRICE', start_date: '', end_date: '', budget_amount: '', budget_hours: '', billing_rate: '', entity_code: '' });
  const [timeForm, setTimeForm] = useState({ project_id: '', employee_name: '', entry_date: '', hours: '', description: '', billable: true });

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };
  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };

  const load = useCallback(async () => {
    setLoading(true);
    const [listRes, sumRes] = await Promise.all([
      authFetch(withEntityParam(API, entity)),
      authFetch(withEntityParam(`${API}/summary`, entity)),
    ]);
    if (listRes.ok) { const d = await listRes.json(); setProjects(d.items || []); }
    if (sumRes.ok) setSummary(await sumRes.json());
    setLoading(false);
  }, [entity]);

  const loadTime = useCallback(async () => {
    const res = await authFetch(withEntityParam(`${API}/time-entries/list`, entity));
    if (res.ok) { const d = await res.json(); setTimeEntries(d.items || []); }
  }, [entity]);

  useEffect(() => { load(); loadTime(); }, [load, loadTime]);

  const handleCreate = async () => {
    const payload = { ...form, budget_amount: parseFloat(form.budget_amount) || 0, budget_hours: parseFloat(form.budget_hours) || 0, billing_rate: form.billing_rate ? parseFloat(form.billing_rate) : undefined };
    const res = await authFetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (res.ok) { showToast('Project created', true); setShowCreate(false); load(); }
    else { const e = await res.json(); showToast(e.detail || 'Error', false); }
  };

  const logTime = async () => {
    const payload = { ...timeForm, project_id: parseInt(timeForm.project_id), hours: parseFloat(timeForm.hours) || 0 };
    const res = await authFetch(`${API}/time-entries`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (res.ok) { showToast('Time logged', true); setTimeForm({ project_id: '', employee_name: '', entry_date: '', hours: '', description: '', billable: true }); loadTime(); }
    else { const e = await res.json(); showToast(e.detail || 'Error', false); }
  };

  const showPnl = async (id: number) => {
    if (pnlId === id) { setPnlId(null); setPnl(null); return; }
    const res = await authFetch(`${API}/${id}/pnl`);
    if (res.ok) { setPnl(await res.json()); setPnlId(id); }
  };

  const handleAction = async (id: number, action: string) => {
    const res = await authFetch(`${API}/${id}/${action}`, { method: 'POST' });
    if (res.ok) { showToast(`Project ${action}`, true); load(); } else { showToast('Failed', false); }
  };

  const timeColumns: Column[] = [
    { key: 'entry_date', label: 'Date' },
    { key: 'project_id', label: 'Project' },
    { key: 'employee_name', label: 'Employee', render: (v: any) => v || '-' },
    { key: 'hours', label: 'Hours', align: 'right' },
    { key: 'hourly_rate', label: 'Rate', align: 'right', render: (v: any) => formatAmount(v) },
    { key: 'total_cost', label: 'Cost', align: 'right', render: (v: any) => formatAmount(v) },
    { key: 'description', label: 'Description', render: (v: any) => v || '-' },
    { key: 'status', label: 'Status', render: (v: any) => <StatusBadge status={v} /> },
  ];

  return (
    <PageLayout
      title="Project Costing"
      subtitle="WBS, time tracking, budget vs actuals, project P&L"
      action={{ label: '+ New Project', onClick: () => setShowCreate(true) }}
    >
      {toast && <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: toast.ok ? '#22c55e' : '#ef4444', color: '#fff' }}>{toast.msg}</div>}

      {summary && (
        <KpiGrid items={[
          { label: 'Total Projects', value: String(summary.total_projects) },
          { label: 'Active', value: String(summary.active), color: '#22c55e' },
          { label: 'Total Budget', value: formatAmountCompact(summary.total_budget), color: 'var(--accent)' },
          { label: 'Actual Cost', value: formatAmountCompact(summary.total_actual_cost), color: '#f59e0b' },
          { label: 'Hours Logged', value: String(summary.total_hours_logged) },
        ]} columns={5} />
      )}

      <TabBar
        tabs={[
          { key: 'projects', label: 'Projects' },
          { key: 'timesheet', label: 'Timesheet' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'projects' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              {['Code', 'Name', 'Customer', 'Type', 'Budget', 'Actual Cost', 'Hours', 'Completion', 'Status', 'Actions'].map(h => <th key={h} style={{ ...thStyle, textAlign: 'left' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={10} style={{ padding: 32, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</td></tr> :
              projects.length === 0 ? <tr><td colSpan={10} style={{ padding: 32, textAlign: 'center', color: 'var(--text-hint)' }}>No projects yet.</td></tr> :
              projects.map(p => (
                <>
                  <tr key={p.id} style={{ borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }} onClick={() => showPnl(p.id)}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{p.project_code}</td>
                    <td style={tdStyle}>{p.project_name}</td>
                    <td style={tdStyle}>{p.customer_name || '-'}</td>
                    <td style={tdStyle}>{p.project_type}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(p.budget_amount)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(p.actual_cost)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{p.actual_hours}/{p.budget_hours}</td>
                    <td style={tdStyle}><div style={{ height: 6, background: 'var(--border-default)', borderRadius: 3, overflow: 'hidden' }}><div style={{ width: `${p.completion_pct}%`, height: '100%', background: '#22c55e', borderRadius: 3 }} /></div><span style={{ fontSize: 10 }}>{p.completion_pct}%</span></td>
                    <td style={tdStyle}><StatusBadge status={p.lifecycle_status} /></td>
                    <td style={tdStyle}>
                      {p.lifecycle_status === 'PLANNING' && <ActionButton label="Activate" onClick={e => { e.stopPropagation(); handleAction(p.id, 'activate'); }} variant="success" size="sm" />}
                      {p.lifecycle_status === 'ACTIVE' && <ActionButton label="Complete" onClick={e => { e.stopPropagation(); handleAction(p.id, 'complete'); }} size="sm" />}
                    </td>
                  </tr>
                  {pnlId === p.id && pnl && (
                    <tr key={`${p.id}-pnl`}><td colSpan={10} style={{ padding: 0 }}>
                      <div style={{ padding: '16px 20px', background: 'var(--bg-hover)' }}>
                        <KpiGrid items={[
                          { label: 'Revenue', value: formatAmountCompact(pnl.revenue) },
                          { label: 'Cost', value: formatAmountCompact(pnl.cost) },
                          { label: 'Margin', value: `${formatAmountCompact(pnl.margin)} (${pnl.margin_pct}%)`, color: pnl.margin >= 0 ? '#22c55e' : '#ef4444' },
                          { label: 'Budget Variance', value: formatAmountCompact(pnl.budget_variance), color: pnl.budget_variance >= 0 ? '#22c55e' : '#ef4444' },
                          { label: 'Hours Var', value: `${pnl.hours_variance}h` },
                        ]} columns={5} />
                      </div>
                    </td></tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'timesheet' && (
        <div>
          <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>Log Time</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr) auto', gap: 8, alignItems: 'end' }}>
              <div><label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Project *</label>
                <select style={inputStyle} value={timeForm.project_id} onChange={e => setTimeForm({ ...timeForm, project_id: e.target.value })}>
                  <option value="">Select...</option>{projects.map(p => <option key={p.id} value={p.id}>{p.project_code}</option>)}
                </select></div>
              <div><label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Employee</label><input style={inputStyle} value={timeForm.employee_name} onChange={e => setTimeForm({ ...timeForm, employee_name: e.target.value })} placeholder="Name" /></div>
              <div><label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Date</label><input style={inputStyle} type="date" value={timeForm.entry_date} onChange={e => setTimeForm({ ...timeForm, entry_date: e.target.value })} /></div>
              <div><label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Hours *</label><input style={inputStyle} type="number" step="0.25" value={timeForm.hours} onChange={e => setTimeForm({ ...timeForm, hours: e.target.value })} placeholder="0" /></div>
              <div><label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Description</label><input style={inputStyle} value={timeForm.description} onChange={e => setTimeForm({ ...timeForm, description: e.target.value })} placeholder="Task..." /></div>
              <ActionButton label="Log" onClick={logTime} disabled={!timeForm.project_id || !timeForm.hours} />
            </div>
          </div>
          <DataTable columns={timeColumns} rows={timeEntries} rowKey="id" emptyMessage="No time entries yet." />
        </div>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Project">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {[['Project Code *', 'project_code', 'PRJ-001'], ['Project Name *', 'project_name', 'Website Redesign'], ['Customer', 'customer_name', 'Acme Corp'], ['Manager', 'project_manager_name', 'John Smith'], ['Start Date', 'start_date', ''], ['End Date', 'end_date', ''], ['Budget Amount', 'budget_amount', '0'], ['Budget Hours', 'budget_hours', '0'], ['Billing Rate (T&M)', 'billing_rate', '150'], ['Entity Code', 'entity_code', 'USA']].map(([label, field, ph]) => (
            <div key={field as string}><label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>{label}</label><input style={inputStyle} value={(form as any)[field as string]} onChange={e => setForm({ ...form, [field as string]: e.target.value })} placeholder={ph as string} type={['budget_amount', 'budget_hours', 'billing_rate'].includes(field as string) ? 'number' : (['start_date', 'end_date'].includes(field as string) ? 'date' : 'text')} /></div>
          ))}
          <div><label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Type</label><select style={inputStyle} value={form.project_type} onChange={e => setForm({ ...form, project_type: e.target.value })}><option value="FIXED_PRICE">Fixed Price</option><option value="TIME_AND_MATERIAL">Time & Material</option><option value="RETAINER">Retainer</option></select></div>
        </div>
        <div style={{ marginTop: 16, textAlign: 'right' }}><ActionButton label="Create Project" onClick={handleCreate} /></div>
      </Modal>
    </PageLayout>
  );
}
